/**
 * PosseAppSwitcher — dropdown listing every app under the Parlay Posse
 * brand. Relative URLs stay inside React Router; absolute URLs use normal
 * browser navigation so future subdomain apps can perform a fresh SSO read.
 * "Coming Soon" apps show a disabled pill so users know the empire is growing.
 *
 * Lives in the navbar's right-side cluster, next to the user menu.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  SquaresFour,
  Target,
  DiceFive,
  Spade,
  Club,
  CaretDown,
  CheckCircle,
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map backend `icon` strings → phosphor components
const ICONS = { Target, DiceFive, Spade, Club };

export default function PosseAppSwitcher() {
  const [apps, setApps] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/posse/apps`);
        setApps(data.apps || []);
      } catch (_) { /* non-blocking */ }
    })();
  }, []);

  if (apps.length <= 1) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="posse-app-switcher-trigger"
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/5 transition-colors"
          aria-label="Switch Posse app"
        >
          <SquaresFour size={18} weight="bold" />
          <CaretDown size={10} weight="bold" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-72 bg-[#0a0a0c] border border-white/10 p-2"
        data-testid="posse-app-switcher-menu"
      >
        <DropdownMenuLabel className="text-[10px] font-heading font-black uppercase tracking-[0.2em] text-white/40 px-2 py-1.5">
          Posse Apps · One Pro pass
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/5" />
        <ul className="space-y-1 mt-1">
          {apps.map((app) => {
            const Icon = ICONS[app.icon] || Target;
            const inner = (
              <div className="flex items-center gap-3 px-2 py-2.5 rounded-md group">
                <div
                  className="shrink-0 h-9 w-9 rounded-md flex items-center justify-center"
                  style={{ background: `${app.color}18`, border: `1px solid ${app.color}40` }}
                >
                  <Icon size={18} weight="fill" style={{ color: app.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-heading font-bold text-sm text-white truncate">
                      {app.name}
                    </span>
                    {app.current && (
                      <CheckCircle size={12} weight="fill" className="text-[#34C759]" />
                    )}
                  </div>
                  <p className="text-[11px] text-white/40 truncate">{app.tagline}</p>
                </div>
                {!app.live && (
                  <span className="shrink-0 px-1.5 py-0.5 rounded-sm bg-white/5 text-[9px] font-heading font-black uppercase tracking-wider text-white/50">
                    Soon
                  </span>
                )}
              </div>
            );
            const interactive = app.live && !app.current;
            const isInternal = app.url?.startsWith('/');
            return (
              <li key={app.id} data-testid={`posse-app-${app.id}`}>
                {interactive && isInternal ? (
                  <DropdownMenuItem asChild className="p-0 focus:bg-white/[0.04]">
                    <Link
                      to={app.url}
                      className="block hover:bg-white/[0.04] rounded-md transition-colors"
                    >
                      {inner}
                    </Link>
                  </DropdownMenuItem>
                ) : interactive ? (
                  <DropdownMenuItem asChild className="p-0 focus:bg-white/[0.04]">
                    <a
                      href={app.url}
                      className="block hover:bg-white/[0.04] rounded-md transition-colors"
                    >
                      {inner}
                    </a>
                  </DropdownMenuItem>
                ) : (
                  <div className={app.current ? '' : 'opacity-60 cursor-not-allowed'}>
                    {inner}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
